import { useQueryClient } from '@tanstack/react-query';
import type { ContentObject, Event } from 'models';
import { useCallback } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { updateBookmarksFeed } from 'features/bookmark/utils';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useUpdateEventBookmark, useUpdateNodeEventBookmark } from 'features/events/hooks';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { useUpdateGivingBookmark } from 'features/givings/hooks';
import { getContentObjectId } from '../../utils';
import { useContentObjectCache } from '../cache';

export type BookmarkMutationParams =
  | {
      contentObject: ContentObject;
      type: 'rails';
    }
  | {
      event: Event;
      type: 'node';
    };

/**
 * @deprecated Introduced as part of the #87353 feed refactor to manually patch the React Query cache
 * after each mutation. This ad-hoc cache-patching approach needs a better long-term solution.
 */
export const useUpdateBookmark = () => {
  const cache = useContentObjectCache();
  const queryClient = useQueryClient();
  const updateEventBookmark = useUpdateEventBookmark();
  const updateGivingBookmark = useUpdateGivingBookmark();
  const updateNodeEventBookmark = useUpdateNodeEventBookmark();

  return useCallback(
    (params: BookmarkMutationParams) => {
      if (params.type === 'node') {
        updateNodeEventBookmark({ event: params.event });
        return;
      }

      const { contentObject } = params;

      // These call genuinely different mutations/backends (e.g. a separate giving detail cache, or an
      // unimplemented video/comment bookmark API), not different cache logic, so they stay separate.
      switch (contentObject.type) {
        case 'event':
          updateEventBookmark({ contentObject });
          return;
        case 'giving':
          updateGivingBookmark({ contentObject });
          return;
        case 'video':
          // TODO: Implement video cache bookmarking logic
          throw new Error('Not implemented');
        case 'comment':
          // TODO: Implement comment cache bookmarking logic
          throw new Error('Not implemented');
        case 'activity':
        case 'kudo':
        case 'poll':
        case 'post':
        case 'withings_order_post':
          break;
      }

      const isBookmarked = !contentObject.bookmarked;

      cache.patch(contentObject, (item) => ({ ...item, bookmarked: isBookmarked }));
      updateBookmarksFeed({
        id: getContentObjectId(contentObject),
        insertableItem: { ...contentObject, bookmarked: isBookmarked },
        isBookmarked,
        queryClient,
      });
    },
    [cache, queryClient, updateEventBookmark, updateGivingBookmark, updateNodeEventBookmark],
  );
};
